import { useDebounceCallback } from 'custom_hooks/useDebounceCallback';
import { CSSProperties, forwardRef, ReactNode, useEffect, useMemo, useRef } from 'react';
import { mergeRefs } from 'utils/mergeRefs';

type InfiniteScrollDirection = 'top' | 'bottom';

export type InfiniteScrollOptions = Readonly<{
    /**
     * Callback requesting more data for container.
     */
    onLoadMore: (() => void) | undefined;

    /**
     * If the container should fire onLoadMore callback when bottom/top reached.
     */
    enabled: boolean;

    /**
     * Indicates if more results are being loaded.  This prevents the container
     * from requesting more items when items are currently being loaded.
     */
    isLoading: boolean;

    /**
     * Component children.
     */
    children: ReactNode;

    /**
     * Optional classes.
     */
    className?: string;

    /**
     * Optional styles.
     */
    style?: CSSProperties;

    /**
     * ARIA text label for this element.
     */
    'aria-label'?: string;

    /**
     * In which direction the container bounds should be checked.
     */
    infiniteScrollDirection?: InfiniteScrollDirection;
}>;

const debounceTimeMs = 16; // ~60fps
const threshold = 0.25;

/**
 * Component used that alerts when the bottom of a container becomes visible.
 *
 * @param param0 configuration options
 */
const InfiniteScroll = forwardRef<HTMLDivElement, InfiniteScrollOptions>(
    (
        {
            onLoadMore,
            enabled,
            isLoading,
            children,
            className,
            style,
            'aria-label': ariaLabel,
            infiniteScrollDirection = 'bottom',
        },
        ref
    ) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const mergedRefs = useMemo(() => mergeRefs(containerRef, ref), [ref]);

        const checkBounds = useDebounceCallback(debounceTimeMs, () => {
            const node = containerRef.current;
            const shouldLoadMore =
                node &&
                (infiniteScrollDirection === 'bottom'
                    ? Math.floor(
                          node.scrollHeight -
                              node.scrollTop -
                              node.clientHeight * threshold
                      ) <= node.clientHeight
                    : node.scrollTop < node.clientHeight * threshold);

            if (onLoadMore && enabled && !isLoading && shouldLoadMore) {
                onLoadMore();
            }
        });

        useEffect(() => {
            const node = containerRef.current;
            node?.addEventListener('scroll', checkBounds, { passive: true });
            return () => node?.removeEventListener('scroll', checkBounds);
        }, [checkBounds]);

        useEffect(() => {
            checkBounds();
        }, [enabled, isLoading, checkBounds]);

        return (
            <div
                className={`full-height scrollable ${className ?? ''}`}
                ref={mergedRefs}
                style={style}
                aria-label={ariaLabel}
            >
                {children}
            </div>
        );
    }
);

export { InfiniteScroll };
